import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { DataService } from './data.service';
import { environment } from '../../environments/environment';
import { NativeIntegrationService } from './native-integration.service';
import { Router } from '@angular/router';
import { AnalyticsService } from 'src/core/services/analytics.service';
import { B2BService } from 'src/core/services/b2b.service';
import { hasBiometrics } from 'src/util';
import {
  AppBridgeErrorCodes,
  AppBridgeResponseCodes,
} from '@hg-com/xwc-js-bridge';
import { ToastrService } from 'ngx-toastr';
import i18next from 'i18next';

@Injectable({
  providedIn: 'root',
})

export class AuthService {
  accessToken!: string;
  isAuthenticated$ = new BehaviorSubject<boolean>(false);
  isAuthenticatedOffline$ = new BehaviorSubject<boolean>(false);
  hasPin$ = new BehaviorSubject<boolean>(false);
  refreshToken!: string;
  apiUrl = environment.apiBaseUrl;
  route = '/core-api/v1';
  contextNative$ = new BehaviorSubject(false);
  authKeys: any;
  username$ = new BehaviorSubject<string | null>(null);
  password!: string;
  sessionToken!: string | undefined;
  public error: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public communityToken = new BehaviorSubject<{
    osit: string;
    sid: string;
    username: string;
  } | null>(null);
  public didLogin = true;
  isLoadingSession = false;

  constructor(
    private http: HttpClient,
    public dataService: DataService,
    protected nativeIntegrationService: NativeIntegrationService,
    private router: Router,
    private analyticsService: AnalyticsService,
    private b2bService: B2BService,
    private toastr: ToastrService
  ) {
    this.initRoute().then();
    this.getCommunityToken().then((x) => {
      this.communityToken.next(x);
    });
    this.checkIfLoggedIn().then((authenticated) => {
      this.isAuthenticated$.next(authenticated);
    });
  }

  ngOnInit(): void {
    if (this.nativeIntegrationService.isNative$.getValue()) {
      this.getSessionToken().then();
    }
  }

  async checkIfLoggedIn(): Promise<boolean> {
    const token = await this.loadAuthenticated();
    console.log('[AUTH SERVICE]: Login Check local storage: ' + token);
    const username = await this.loadUsername();
    console.log('[AUTH SERVICE]: Login Check username: ' + username);
    const hasPin = await this.hasPin();
    console.log('[AUTH SERVICE]: Login Check hasPin: ' + hasPin);
    return token === 'true';
  }

  getSessionToken(): Promise<any> {
    return this.nativeIntegrationService.storage
      .secureStoreLoad$('sessionToken', {
        useBiometrics: false,
        usePinCode: 'false',
      })
      .then((token) => {
        console.log(
          '[AUTH SERVICE]: get session token from secure store: ' + token
        );
        if (token.data !== null) {
          this.sessionToken = token.data.raw;
        }
      });
  }

  public initRoute(): Promise<any> {
    return this.nativeIntegrationService.spinTillReady().then(() => {
      if (this.nativeIntegrationService.isNative$.getValue()) {
        this.apiUrl =
          `http://localhost:${this.nativeIntegrationService.native.port}/xwc/atum` +
          this.route;
        this.contextNative$.next(true);
        console.log('[AUTH SERVICE]: Init Route native -> ' + this.apiUrl);
      } else {
        this.apiUrl = environment.apiBaseUrl + this.route;
        console.log('[AUTH SERVICE]: Init Route web version -> ' + this.apiUrl);
      }
    });
  }

  public async getSalesForceToken() {
    await this.nativeIntegrationService.spinTillReady();
    let token, tokenCreated;
    if (this.nativeIntegrationService.isNative$.getValue()) {
      token = (
        await this.nativeIntegrationService.storage.secureStoreLoad$(
          'sftoken',
          { useBiometrics: false, usePinCode: 'false' }
        )
      ).data?.raw;
      if (!token) {
        return null;
      }
      tokenCreated = (
        await this.nativeIntegrationService.storage.secureStoreLoad$(
          'sftokencreated',
          { useBiometrics: false, usePinCode: 'false' }
        )
      ).data?.raw;
      if (!tokenCreated) {
        return null;
      }
    } else {
      token = localStorage.getItem('sftoken');
      if (!token) {
        return null;
      }
      tokenCreated = localStorage.getItem('sftokencreated');
      if (!tokenCreated) {
        return null;
      }
    }
    const d = new Date(tokenCreated);
    const now = new Date();
    /*if (
      d.getDate() !== now.getDate() ||
      d.getMonth() !== now.getMonth() ||
      d.getFullYear() !== now.getFullYear()
    ) {
      return null;
    }*/
    // Dif bilden
    // 300000 = 5 min
    // 86400000 = 24 h
    if (now.getTime() - d.getTime() > 86400000) {
      return null;
    }
    return token;
  }

  async authenticateSalesForce(username: string, password: string) {
    await this.nativeIntegrationService.spinTillReady();
    const token = await this.b2bService.getToken(username, password);
    if (this.nativeIntegrationService.isNative$.getValue()) {
      await this.nativeIntegrationService.storage.secureStoreSave$(
        token.sessionId,
        'sftoken',
        false,
        'false'
      );
      await this.nativeIntegrationService.storage.secureStoreSave$(
        new Date().toISOString(),
        'sftokencreated',
        false,
        'false'
      );
    } else {
      localStorage.setItem('sftoken', token.sessionId);
      localStorage.setItem('sftokencreated', new Date().toISOString());
    }

    const communityToken = await this.b2bService.getCommunityPw(
      token.sessionId,
      username,
      password
    );
    if (!communityToken) {
      this.setHasB2BAccess('false');
      return;
    }
    this.setHasB2BAccess('true');
    this.communityToken.next(communityToken);
    if (this.nativeIntegrationService.isNative$.getValue()) {
      await this.nativeIntegrationService.storage.secureStoreSave$(
        JSON.stringify(communityToken),
        'communityToken',
        false,
        'false'
      );
    } else {
      localStorage.setItem('communityToken', JSON.stringify(communityToken));
    }
  }

  async getCommunityToken(): Promise<{
    osit: string;
    sid: string;
    username: string;
  } | null> {
    await this.nativeIntegrationService.spinTillReady();
    if (this.nativeIntegrationService.isNative$.getValue()) {
      const token = (
        await this.nativeIntegrationService.storage.secureStoreLoad$(
          'communityToken',
          { useBiometrics: false, usePinCode: 'false' }
        )
      ).data?.raw;
      if (!token) {
        return null;
      }

      return JSON.parse(token);
    } else {
      const token = localStorage.getItem('communityToken');
      if (!token) {
        return null;
      }
      return JSON.parse(token);
    }
  }

  // Web authentication
  authenticate(username: string, password: string) {
    this.password = password;
    const body = {
      username,
      password,
    };
    this.http
      .post<any>(this.apiUrl + '/auth/login', body, { withCredentials: true })
      .subscribe(
        async (data) => {
          console.log('[AUTH SERVICE]: Auth web version success');
          // Nur für die analytics
          this.didLogin = true;
          console.log(data);
          this.accessToken = data.access_token;
          localStorage.setItem('token', this.accessToken);
          this.refreshToken = data.refresh_token;
          localStorage.setItem('refreshToken', this.refreshToken);
          if (this.accessToken || this.accessToken !== '') {
            await this.saveUsername(username);
            this.error.next(false);
            await this.saveAuthenticated(true);
          }
          this.authenticateSalesForce(username, password).then(() => {
            console.log('auth salesforce successfull');
          });
        },
        (error) => {
          if (!this.error.getValue()) {
            console.log('[AUTH SERVICE]: Auth web version error');
            console.log('error');
            console.log(error);
            this.error.next(true);
          }
        }
      );
  }

  async authenticateNative(username: string, password: string) {
    this.password = password;
    console.log('[AUTH SERVICE]: Auth native');

    // secureStoreSave$(value: string, key?: string, useBiometrics?: boolean, usePinCode?: string): Promise<IBridgeAckOrError>;
    this.nativeIntegrationService.storage
      .secureStoreSave$(password, 'password', false, 'false')
      .then((r) => {
        console.log(r);
      });
    await this.saveUsername(username);
    this.dataService.loading$.next(true);

    const clientAuthKeys = this.nativeIntegrationService.clientAuth
      .getKeys$()
      .then((res) => {
        console.log('[AUTH SERVICE]: Auth native clientAuth.getKeys$:');
        console.log(res);
        this.authKeys = res;

        this.http
          .post<{ session_token: string }>(
            this.apiUrl + `/client-auth/combined-login`,
            {
              clientKey: this.authKeys.data.clientKey,
              deviceId: this.authKeys.data.deviceId,
              deviceInfo: this.authKeys.data.deviceInfo,
              username: username,
              password: this.password,
            }
          )
          .subscribe(
            async (result: { session_token: string }) => {
              console.log(
                '[AUTH SERVICE]: Auth native combined login success. Session Token -> ' +
                  JSON.stringify(result)
              );
              this.dataService.loading$.next(false);
              this.didLogin = true;
              this.sessionToken = result.session_token;
              await this.nativeIntegrationService.storage.secureStoreSave$(
                this.sessionToken,
                'sessionToken',
                false,
                'false'
              );
              this.isAuthenticatedOffline$.next(true);
              await this.saveUsername(username);
              await this.saveAuthenticated(true);
              console.log(
                '[AUTH SERVICE]: Auth native combined login success. Is Authenticated Offline -> true'
              );
              this.authenticateSalesForce(username, password).then(() => {
                console.log('auth salesforce successfull');
              });
            },
            (error) => {
              if (!this.error.getValue()) {
                console.log(
                  '[AUTH SERVICE]: Auth native combined login error.'
                );
                console.log(error);
                this.error.next(true);
              }
              this.dataService.loading$.next(false);
            }
          );
      });
  }

  validateLogin(pin: string): void {
    if (this.sessionToken) {
      this.nativeIntegrationService.clientAuth
        .validateLogin$({
          store: true,
          sessionToken: this.sessionToken,
          useBiometrics: true,
          usePinCode: pin,
        })
        .then(async () => {
          console.log('[AUTH SERVICE]: Auth native validate login success');
          this.error.next(false);
          await this.savePin(pin);
          await this.saveAuthenticated(true);
        })
        .catch((error) => {
          console.error(error);
        });
    }
  }

  async clearPin() {
    await this.nativeIntegrationService.spinTillReady();
    if (this.nativeIntegrationService.isNative$.getValue()) {
      await this.nativeIntegrationService.storage.secureStoreDelete$('pin');
      this.hasPin$.next(false);
    }
  }

  async savePin(pin: string) {
    await this.nativeIntegrationService.spinTillReady();
    if (this.nativeIntegrationService.isNative$.getValue()) {
      await this.nativeIntegrationService.storage.secureStoreSave$(
        pin,
        'pin',
        false,
        'false'
      );
      this.hasPin$.next(true);
    }
  }

  async hasPin() {
    await this.nativeIntegrationService.spinTillReady();
    if (this.nativeIntegrationService.isNative$.getValue()) {
      const x = await this.nativeIntegrationService.storage.secureStoreLoad$(
        'pin',
        {
          useBiometrics: false,
          usePinCode: 'false',
        }
      );
      if (x.data !== null) {
        this.hasPin$.next(true);
        return x.data.raw!;
      } else {
        this.hasPin$.next(false);
        return null;
      }
    } else {
      return true;
    }
  }

  async hasUsername() {
    await this.nativeIntegrationService.spinTillReady();
    let token;
    if (this.nativeIntegrationService.isNative$.getValue()) {
      token = (
        await this.nativeIntegrationService.storage.secureStoreLoad$(
          'username',
          { useBiometrics: false, usePinCode: 'false' }
        )
      ).data?.raw;
      console.log('[AUTH SERVICE]: Loaded Username Native: ' + token);
    } else {
      token = localStorage.getItem('username');
      console.log('[AUTH SERVICE]: Loaded Username: ' + token);
    }
    return token ?? null;
  }

  async loadUsername() {
    console.log('[AUTH SERVICE]: Load Username');
    const token = await this.hasUsername();
    this.username$.next(token ?? null);
    return token ?? null;
  }

  async saveUsername(username: string | null) {
    await this.nativeIntegrationService.spinTillReady();
    if (this.nativeIntegrationService.isNative$.getValue()) {
      if (username !== null) {
        console.log('[AUTH SERVICE]: Save Username: ' + username);
        await this.nativeIntegrationService.storage.secureStoreSave$(
          username,
          'username',
          false,
          'false'
        );
      } else {
        console.log('[AUTH SERVICE]: Delete Username');
        await this.nativeIntegrationService.storage.secureStoreDelete$(
          'username'
        );
      }
    } else {
      if (username !== null) {
        console.log('[AUTH SERVICE]: Save Username: ' + username);
        localStorage.setItem('username', username);
      } else {
        console.log('[AUTH SERVICE]: Delete Username');
        localStorage.removeItem('username');
      }
    }
    this.username$.next(username);
  }

  async loadAuthenticated() {
    console.log('[AUTH SERVICE]: Load Authenticated');
    await this.nativeIntegrationService.spinTillReady();
    let token;
    if (this.nativeIntegrationService.isNative$.getValue()) {
      token = (
        await this.nativeIntegrationService.storage.secureStoreLoad$(
          'authenticated',
          { useBiometrics: false, usePinCode: 'false' }
        )
      ).data?.raw;
      console.log('[AUTH SERVICE]: Loaded Authenticated Native: ' + token);
    } else {
      token = localStorage.getItem('authenticated');
      console.log('[AUTH SERVICE]: Loaded Authenticated: ' + token);
    }
    return token;
  }

  async saveAuthenticated(authenticated: boolean) {
    await this.nativeIntegrationService.spinTillReady();

    if (this.nativeIntegrationService.isNative$.getValue()) {
      console.log('[AUTH SERVICE]: Save Authenticated: ' + authenticated);
      await this.nativeIntegrationService.storage.secureStoreSave$(
        authenticated ? 'true' : 'false',
        'authenticated',
        false,
        'false'
      );
    } else {
      console.log('[AUTH SERVICE]: Save Authenticated: ' + authenticated);
      localStorage.setItem('authenticated', authenticated ? 'true' : 'false');
    }
    this.isAuthenticated$.next(authenticated);
  }

  async setHasB2BAccess(status: string) {
    await this.nativeIntegrationService.spinTillReady();
    if (this.nativeIntegrationService.isNative$.getValue()) {
      await this.nativeIntegrationService.storage.secureStoreSave$(
        status,
        'hasB2BAccess',
        false,
        'false'
      );
    } else {
      localStorage.setItem('hasB2BAccess', status);
    }
  }

  async loadSessionPin(pin: string) {
    console.log('[LOGIN]: Load Session Pin');
    await this.nativeIntegrationService.spinTillReady();
    const username = await this.hasUsername();
    if (!username) {
      console.log('[LOGIN]: No username found');
      return Promise.resolve(false);
    }
    if (this.isLoadingSession) {
      return;
    }
    this.dataService.loading$.next(true);
    this.isLoadingSession = true;
    return this.nativeIntegrationService.clientAuth
      .loadSession$({ usePinCode: pin })
      .then(async (result) => {
        this.isLoadingSession = false;
        this.dataService.loading$.next(false);
        console.log('[LOGIN]: Load Session Pin result -> ' + result);
        console.log(result);
        const success = result.status === AppBridgeResponseCodes.Ok;
        console.log('Success: ' + success);
        console.log('AppBridgeResponseCodes: ' + AppBridgeResponseCodes);
        if (success) {
          console.log('[LOGIN]: Biometric Auth success');
          await this.getSessionToken();
          this.isAuthenticatedOffline$.next(true);
          await this.loadUsername();
          await this.saveAuthenticated(true);
          console.log(
            '[LOGIN]: Load Session Pin success. Is Authenticated Offline -> true'
          );
        }
      })
      .catch((error) => {
        this.dataService.loading$.next(false);
        this.isLoadingSession = false;
        console.log('[LOGIN]: Load Session Pin error -> ' + error);
        console.log(error);
      });
  }

  async loadSession() {
    console.log('[LOGIN]: Load Session');
    if (this.isLoadingSession) {
      console.log('[LOGIN]: Load Session already loading');
      return;
    }
    this.isLoadingSession = true;

    await this.nativeIntegrationService.spinTillReady();
    const supportsBiometrics = await hasBiometrics(
      this.nativeIntegrationService
    );
    console.log('[LOGIN] Support Biometric auth? ' + supportsBiometrics);
    if (supportsBiometrics) {
      this.dataService.loading$.next(true);
      return this.nativeIntegrationService.clientAuth
        .loadSession$({ useBiometrics: true })
        .then(async (result) => {
          this.dataService.loading$.next(false);
          this.isLoadingSession = false;
          console.log('[LOGIN]: Load Session result -> ' + result);
          console.log(result);
          const biometricsActive =
            result.status === AppBridgeErrorCodes.AuthRequired;
          console.log('Biometrics Active: ' + biometricsActive);
          console.log('AppBridgeErrorCodes: ' + AppBridgeErrorCodes);
          if (result.status === 1) {
            console.log('[LOGIN]: Biometric Auth success');
            await this.getSessionToken();
            this.isAuthenticatedOffline$.next(true);
            await this.loadUsername();
            await this.saveAuthenticated(true);
            console.log(
              '[LOGIN]: Load Session success. Is Authenticated Offline -> true'
            );
          } else {
            // this.router.navigate(['/auth/biometric']);
            console.log('[LOGIN]: Biometric Auth failed');
            this.isAuthenticatedOffline$.next(false);
            await this.saveAuthenticated(false);
            this.saveUsername(null);
          }
        })
        .catch((error) => {
          this.dataService.loading$.next(false);
          this.isLoadingSession = false;
          console.log('[LOGIN]: Load Session error -> ' + error);
          console.log(error);
          this.toastr.error(
            i18next.t('login.login-error-title'),
            i18next.t('login.login-error-txt')
          );
        });
    } else {
      this.isLoadingSession = false;
      console.log('[LOGIN]: Biometric Auth not supported');
      // this.router.navigate(['/auth/biometric']);
      return Promise.resolve(false);
    }
  }
}
